import type { ChipColor } from '@/types/chip';
import type { TutoringAgreementRecord } from '@/types/entities';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow, Chip, TablePagination, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Description } from '@mui/icons-material';
import { useGetList } from 'react-admin';
import { useState } from 'react';
import { ListPageHeader } from '@/components/ListPageHeader';

interface TutorItem {
  id: string;
  legalEntity?: { firstName?: string; lastName?: string };
}

interface ClientItem {
  id: string;
  name?: string;
}

const STATUSES = ['Draft', 'Active', 'Expired', 'Terminated'];

const STATUS_COLORS: Record<string, ChipColor> = {
  Draft: 'default',
  Active: 'success',
  Expired: 'error',
  Terminated: 'warning',
};

export const TutoringAgreementList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filters, setFilters] = useState<Record<string, string | undefined>>({});
  const { data: allAgreements } = useGetList('tutoring-agreements', { pagination: { page: 1, perPage: 1000 }, filter: filters });
  const { data: allTutors } = useGetList('tutors', { pagination: { page: 1, perPage: 1000 } });
  const { data: allClients } = useGetList('clients', { pagination: { page: 1, perPage: 1000 } });

  const agreements = allAgreements?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const resolveTutorName = (agreement: TutoringAgreementRecord) => {
    return agreement.tutorName || '—';
  };

  const resolveClientName = (agreement: TutoringAgreementRecord) => {
    return agreement.clientName || '—';
  };

  const formatShortId = (id: string) => {
    if (id.length > 20) return id.slice(-12);
    return id;
  };

  const stats = {
    total: allAgreements?.length ?? 0,
    active: allAgreements?.filter((a: TutoringAgreementRecord) => a.status === 'Active').length ?? 0,
    expired: allAgreements?.filter((a: TutoringAgreementRecord) => a.status === 'Expired').length ?? 0,
    terminated: allAgreements?.filter((a: TutoringAgreementRecord) => a.status === 'Terminated').length ?? 0,
  };

  return (
    <Box sx={{ p: 2 }}>
      <ListPageHeader
        title="Tutoring Agreements"
        metrics={[
          { label: 'Total', value: String(stats.total), color: 'primary.main' },
          { label: 'Active', value: String(stats.active), color: 'success.main' },
          { label: 'Expired', value: String(stats.expired), color: 'error.main' },
          { label: 'Terminated', value: String(stats.terminated), color: 'warning.main' },
        ]}
      />
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filters.status || ''}
            label="Status"
            onChange={e => setFilters(prev => ({ ...prev, status: e.target.value || undefined }))}
          >
            <MenuItem value="">All</MenuItem>
            {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Tutor</InputLabel>
          <Select
            value={filters.tutorId || ''}
            label="Tutor"
            onChange={e => setFilters(prev => ({ ...prev, tutorId: e.target.value || undefined }))}
          >
            <MenuItem value="">All</MenuItem>
            {allTutors?.map((t: TutorItem) => <MenuItem key={t.id} value={t.id}>{t.legalEntity?.firstName ?? ""} {t.legalEntity?.lastName ?? ""}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Client</InputLabel>
          <Select
            value={filters.clientId || ''}
            label="Client"
            onChange={e => setFilters(prev => ({ ...prev, clientId: e.target.value || undefined }))}
          >
            <MenuItem value="">All</MenuItem>
            {allClients?.map((c: ClientItem) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>
      <Paper sx={{ overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600 }}>Agreement #</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Tutor</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Start Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>End Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {agreements?.map((agreement: TutoringAgreementRecord) => (
              <TableRow
                key={agreement.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/tutoring-agreements/${agreement.id}/show`)}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Description sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="body2" fontWeight={500}>{formatShortId(agreement.id)}</Typography>
                  </Box>
                </TableCell>
                <TableCell><Typography variant="body2">{resolveTutorName(agreement)}</Typography></TableCell>
                <TableCell><Typography variant="body2">{resolveClientName(agreement)}</Typography></TableCell>
                <TableCell><Typography variant="body2">{agreement.effectiveFrom || '—'}</Typography></TableCell>
                <TableCell><Typography variant="body2">{agreement.effectiveTo || '—'}</Typography></TableCell>
                <TableCell><Chip label={agreement.status} size="small" color={STATUS_COLORS[agreement.status]} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={allAgreements?.length ?? 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
        {(!agreements || agreements.length === 0) && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Description sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography color="text.secondary">No tutoring agreements found</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
